import { PublicBreathwork } from '@breath-lab/types';
import { TrainingBenefit, benefitNameEn } from '@breath-lab/types';

export interface BreathworkCardChip {
  id: string;
  label: string;
  emoji?: string;
  kind: 'benefit' | 'difficulty';
}

export interface BreathworkCardChipsResult {
  chips: BreathworkCardChip[];
  hasMore: boolean;
}

function formatDifficulty(level: string): string {
  return level.toLowerCase();
}

export function chooseBreathworkCardChips(
  breathwork: PublicBreathwork,
  benefits: TrainingBenefit[],
): BreathworkCardChipsResult {
  const matchedBenefits = breathwork.benefitsIds
    .map((id) => benefits.find((benefit) => benefit.id === id))
    .filter((benefit): benefit is TrainingBenefit => benefit !== undefined);

  const difficultyChip: BreathworkCardChip | null = breathwork.difficultyLevel
    ? { id: 'difficulty', label: formatDifficulty(breathwork.difficultyLevel), kind: 'difficulty' }
    : null;

  const benefitSlots = difficultyChip ? 2 : 3;
  const benefitChips = matchedBenefits
    .slice()
    .sort((a, b) => benefitNameEn(a).length - benefitNameEn(b).length)
    .slice(0, benefitSlots)
    .map((benefit) => ({
      id: benefit.id,
      label: benefitNameEn(benefit),
      emoji: benefit.emoji,
      kind: 'benefit' as const,
    }));

  const chips = [
    ...(difficultyChip ? [difficultyChip] : []),
    ...benefitChips,
  ];
  const totalAvailableTags = matchedBenefits.length;

  return {
    chips,
    hasMore: totalAvailableTags > benefitChips.length,
  };
}

const GOAL_CHIP_LABELS: Record<string, string> = {
  STRESS_RELIEF: 'stress relief',
  BETTER_SLEEP: 'sleep',
  INCREASED_FOCUS_AND_CONCENTRATION: 'focus',
  INCREASED_ENERGY_LEVELS: 'energy',
  CALMS_BODY_AND_MIND: 'calm',
  MOOD_LIFT: 'mood lift',
  EMOTIONAL_BALANCE: 'emotional balance',
};

export function cardChipLabel(chip: BreathworkCardChip): string {
  if (chip.kind === 'benefit') {
    return GOAL_CHIP_LABELS[chip.id] ?? chip.label.toLowerCase().replace('(hrv)', '(HRV)');
  }
  return chip.label;
}
